import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Heading, Text, HStack, VStack, SimpleGrid, Spinner,
  Badge, Icon, Button, Alert, AlertIcon, Progress, Tooltip,
} from '@chakra-ui/react';
import { FiRefreshCw, FiShield, FiCheckCircle, FiXCircle, FiClock } from 'react-icons/fi';

const API_BASE = '/api/v1';
const MAX_HISTORY = 20;

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function TrafficHealth() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const history = useRef({});   // { svcId: [ { ok, latency_ms, time }, … ] }

  const runCheck = useCallback(async () => {
    setChecking(true);
    try {
      const res = await fetch(`${API_BASE}/traffic/check/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Update history
      data.results.forEach(r => {
        if (!history.current[r.id]) history.current[r.id] = [];
        history.current[r.id].unshift({ ok: r.ok, latency_ms: r.latency_ms, time: new Date() });
        if (history.current[r.id].length > MAX_HISTORY) history.current[r.id].pop();
      });

      setServices(data.results);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
    setChecking(false);
  }, []);

  useEffect(() => {
    runCheck();
    const interval = setInterval(runCheck, 30000);
    return () => clearInterval(interval);
  }, [runCheck]);

  const getHistoryStats = (svcId) => {
    const h = history.current[svcId] || [];
    const checks = h.length;
    const upCount = h.filter(e => e.ok).length;
    const uptime = checks > 0 ? Math.round((upCount / checks) * 100) : 0;
    const latencies = h.filter(e => e.latency_ms != null).map(e => e.latency_ms);
    const avgLat = latencies.length > 0 ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length) : null;
    return { checks, uptime, avgLat, history: h };
  };

  const latencyColor = (ms) => {
    if (ms == null) return 'red';
    if (ms < 300) return 'green';
    if (ms < 1000) return 'yellow';
    return 'red';
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;

  const healthyCount = services.filter(s => s.ok).length;
  const unhealthyCount = services.filter(s => !s.ok).length;
  const allHealthy = unhealthyCount === 0;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Service Health</Heading>
          <Text color="gray.500">
            Per-service health with latency history &middot; Auto-checks every 30s &middot; History: last {MAX_HISTORY} checks
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="blue" onClick={runCheck} isLoading={checking}>
          Check All
        </Button>
      </HStack>

      {/* Overall Banner */}
      <Alert
        status={allHealthy ? 'success' : 'error'}
        borderRadius="lg"
        mb={8}
        variant="subtle"
      >
        <AlertIcon boxSize={6} />
        <Box ml={2}>
          <Text fontWeight="bold" fontSize="lg">
            {allHealthy
              ? 'All systems operational'
              : `${unhealthyCount} service${unhealthyCount > 1 ? 's' : ''} degraded`
            }
          </Text>
          <Text fontSize="sm" color="gray.600">
            {healthyCount} of {services.length} services healthy
          </Text>
        </Box>
      </Alert>

      {/* Service Health Cards */}
      <VStack spacing={4} align="stretch">
        {services.map(svc => {
          const stats = getHistoryStats(svc.id);
          return (
            <Box
              key={svc.id}
              bg="white"
              borderRadius="lg"
              boxShadow="md"
              p={6}
              borderLeft="4px solid"
              borderColor={svc.ok ? 'green.400' : 'red.400'}
              _hover={{ boxShadow: 'lg' }}
              transition="box-shadow 0.2s"
            >
              <HStack justify="space-between" mb={3}>
                <HStack spacing={3}>
                  <Icon
                    as={svc.ok ? FiCheckCircle : FiXCircle}
                    color={svc.ok ? 'green.500' : 'red.500'}
                    boxSize={6}
                  />
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="bold" fontSize="md">{svc.name}</Text>
                    <Text fontSize="xs" fontFamily="mono" color="gray.400">{svc.endpoint}</Text>
                  </VStack>
                </HStack>
                <Badge
                  colorScheme={svc.ok ? 'green' : 'red'}
                  fontSize="sm"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {svc.ok ? 'HEALTHY' : 'DOWN'}
                </Badge>
              </HStack>

              {/* Metrics Row */}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={4}>
                <Box>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold">Latency</Text>
                  <HStack spacing={1}>
                    <Icon as={FiClock} boxSize={3} color={`${latencyColor(svc.latency_ms)}.500`} />
                    <Text fontWeight="bold" color={`${latencyColor(svc.latency_ms)}.600`}>
                      {svc.latency_ms != null ? `${svc.latency_ms} ms` : '—'}
                    </Text>
                  </HStack>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold">Avg Latency</Text>
                  <Text fontWeight="bold">{stats.avgLat != null ? `${stats.avgLat} ms` : '—'}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold">Uptime</Text>
                  <Text fontWeight="bold" color={stats.uptime >= 90 ? 'green.600' : stats.uptime >= 50 ? 'yellow.600' : 'red.600'}>
                    {stats.uptime}%
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold">Checks</Text>
                  <Text fontWeight="bold">{stats.checks}</Text>
                </Box>
              </SimpleGrid>

              {/* Sparkline as coloured progress-like bar */}
              <HStack spacing={1} height="24px" align="flex-end">
                {Array.from({ length: MAX_HISTORY }).map((_, i) => {
                  const entry = stats.history[MAX_HISTORY - 1 - i];
                  if (!entry) {
                    return <Box key={i} w="100%" h="4px" bg="gray.100" borderRadius="sm" />;
                  }
                  const h = entry.ok
                    ? Math.max(6, Math.min(24, Math.round((entry.latency_ms || 100) / 60)))
                    : 24;
                  const color = !entry.ok ? 'red.400' : entry.latency_ms < 300 ? 'green.400' : entry.latency_ms < 1000 ? 'yellow.400' : 'red.400';
                  return (
                    <Tooltip key={i} label={entry.latency_ms != null ? `${entry.latency_ms}ms` : 'fail'} fontSize="xs">
                      <Box w="100%" h={`${h}px`} bg={color} borderRadius="sm" transition="height 0.3s" cursor="pointer" />
                    </Tooltip>
                  );
                })}
              </HStack>

              {svc.error && (
                <Text fontSize="xs" color="red.500" mt={2}>Error: {svc.error}</Text>
              )}
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}

export default TrafficHealth;
